import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import SearchBar from '@/components/feature/SearchBar';
import { genreOptions } from '@/mocks/movies';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
];

const trailingItems = [
  { label: '关于我们', to: '/about' },
  { label: '联系我们', to: '/contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'text-accent-600' : 'text-foreground-700 hover:text-primary-600'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50">
            <i className="ri-movie-2-fill text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">神马影院</span>
            <span className="text-[10px] font-medium tracking-widest text-foreground-400">SHENMA CINEMA</span>
          </span>
        </Link>

        <nav className="hidden lg:flex lg:items-center">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === '/'}>
              {item.label}
            </NavLink>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:text-primary-600"
            >
              分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full w-[420px] pt-2">
                <div className="grid grid-cols-4 gap-1 rounded-lg border border-background-200 bg-background-50 p-3 shadow-sm">
                  {genreOptions.map((genre) => (
                    <Link
                      key={genre}
                      to={`/search?q=${encodeURIComponent(genre)}`}
                      className="rounded-md px-2 py-1.5 text-center text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {trailingItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden w-full max-w-xs lg:block">
          <SearchBar />
        </div>

        <Link
          to="/search"
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-full border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden"
          aria-label="搜索"
        >
          <i className="ri-search-line text-lg"></i>
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-200 text-foreground-600 lg:hidden"
          aria-label="打开菜单"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="mx-auto max-w-[1400px] space-y-3 px-4 py-4 md:px-6">
            <SearchBar />
            <nav className="flex flex-col">
              {[...navItems, ...trailingItems].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm font-medium ${
                      isActive ? 'bg-primary-50 text-primary-700' : 'text-foreground-700'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {genreOptions.slice(0, 10).map((genre) => (
                <Link
                  key={genre}
                  to={`/search?q=${encodeURIComponent(genre)}`}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
                >
                  {genre}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}